// CommandPalette — ⌘K / Ctrl-K. The thing that makes a 24-section console usable.
//
// Without it, reaching a section means finding it in a sidebar of 20+ links;
// reaching a RECORD means finding its section, then its filter, then the row.
// The palette collapses both into typing.
//
// Three result kinds, in priority order:
//   1. Actions   — "Issue a refund", "New blog post". Verbs the operator wants.
//   2. Sections  — navigation. Always available, matched fuzzily.
//   3. Records   — live results from /admin/search, debounced.
//
// Records come last deliberately: they arrive asynchronously, and a list that
// reorders under the keyboard cursor mid-selection causes mis-clicks.

import { cn } from '@fajrarabi/ui';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

export interface CommandItem {
  id: string;
  label: string;
  /** Secondary line: the section, or a record's owner. */
  hint?: string;
  icon?: IconName;
  group: 'Actions' | 'Go to' | 'Results';
  /** Extra words matched against the query but not displayed. */
  keywords?: string;
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Static sections + actions. */
  items: readonly CommandItem[];
  /** Async record results for the current query. */
  results?: readonly CommandItem[];
  loadingResults?: boolean;
  onQueryChange?: (query: string) => void;
}

/**
 * Subsequence match — "blgc" matches "Blog categories". Returns a score
 * (lower is better) or null. Contiguous matches and word-start matches rank
 * above scattered ones, so exact prefixes win.
 */
export function fuzzyScore(text: string, query: string): number | null {
  if (query === '') return 0;
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();

  // Fast path: a direct substring is always the best kind of match.
  const direct = haystack.indexOf(needle);
  if (direct !== -1) return direct === 0 ? 0 : 1 + direct * 0.01;

  let score = 10;
  let cursor = 0;
  for (const char of needle) {
    const found = haystack.indexOf(char, cursor);
    if (found === -1) return null;
    // Gaps cost; a match right after the previous one costs nothing.
    score += found - cursor;
    // Matching at a word boundary is worth a discount.
    if (found === 0 || haystack[found - 1] === ' ') score -= 1;
    cursor = found + 1;
  }
  return score;
}

export function CommandPalette({
  open,
  onOpenChange,
  items,
  results = [],
  loadingResults = false,
  onQueryChange,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      onQueryChange?.('');
    }
  }, [open, onQueryChange]);

  const filtered = useMemo(() => {
    const scored = items
      .map((item) => ({
        item,
        score: fuzzyScore(`${item.label} ${item.keywords ?? ''}`.trim(), query),
      }))
      .filter((entry): entry is { item: CommandItem; score: number } => entry.score !== null)
      .sort((a, b) => a.score - b.score)
      .map((entry) => entry.item);

    // Server results are already relevance-ranked; do not re-sort them.
    return [...scored, ...results];
  }, [items, results, query]);

  // The active index must stay inside the list as it shrinks under typing.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, filtered.length - 1)));
  }, [filtered.length]);

  const grouped = useMemo(() => {
    const order: CommandItem['group'][] = ['Actions', 'Go to', 'Results'];
    const map = new Map<CommandItem['group'], CommandItem[]>();
    for (const item of filtered) {
      const list = map.get(item.group);
      if (list) list.push(item);
      else map.set(item.group, [item]);
    }
    return order
      .filter((group) => map.has(group))
      .map((group) => ({ group, items: map.get(group)! }));
  }, [filtered]);

  const select = useCallback(
    (item: CommandItem) => {
      onOpenChange(false);
      // Defer so the dialog's close/focus-restore finishes before navigation.
      // Running both in the same tick leaves focus on a removed node.
      setTimeout(() => item.onSelect(), 0);
    },
    [onOpenChange],
  );

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(1, filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % Math.max(1, filtered.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) select(item);
    }
  }

  // Keep the highlighted row in view during keyboard navigation.
  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  let flatIndex = -1;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[70] bg-admin-900/40 backdrop-blur-sm" />
        <RadixDialog.Content
          aria-label="Command palette"
          className="fixed left-1/2 top-[12vh] z-[71] w-[min(38rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-admin-200 bg-white shadow-2xl focus:outline-none"
        >
          <RadixDialog.Title className="sr-only">Command palette</RadixDialog.Title>

          <div className="flex items-center gap-2.5 border-b border-admin-200 px-4">
            <Icon name="search" className="h-4 w-4 shrink-0 text-admin-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
                onQueryChange?.(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search sections, records and actions…"
              aria-label="Search sections, records and actions"
              className="h-12 w-full bg-transparent text-sm text-admin-900 placeholder:text-admin-400 focus:outline-none"
            />
            {loadingResults && (
              <span
                className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-admin-200 border-t-brand-500"
                aria-label="Searching"
              />
            )}
            <kbd className="hidden shrink-0 rounded border border-admin-200 px-1.5 py-0.5 text-[10px] font-medium text-admin-400 sm:block">
              ESC
            </kbd>
          </div>

          <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-admin-400">
                {loadingResults ? 'Searching…' : 'No matches'}
              </p>
            ) : (
              grouped.map(({ group, items: groupItems }) => (
                <div key={group} className="mb-1 last:mb-0">
                  <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-admin-400">
                    {group}
                  </p>
                  {groupItems.map((item) => {
                    flatIndex += 1;
                    const isActive = flatIndex === activeIndex;
                    const myIndex = flatIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-active={isActive}
                        onMouseEnter={() => setActiveIndex(myIndex)}
                        onClick={() => select(item)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start transition-colors',
                          isActive ? 'bg-brand-50 text-brand-700' : 'text-admin-700',
                        )}
                      >
                        {item.icon && (
                          <Icon
                            name={item.icon}
                            className={cn(
                              'h-4 w-4',
                              isActive ? 'text-brand-500' : 'text-admin-400',
                            )}
                          />
                        )}
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {item.label}
                        </span>
                        {item.hint && (
                          <span className="shrink-0 truncate text-xs text-admin-400">
                            {item.hint}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center gap-3 border-t border-admin-100 bg-admin-50 px-4 py-2 text-[11px] text-admin-400">
            <span>
              <kbd className="font-sans font-semibold">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="font-sans font-semibold">↵</kbd> open
            </span>
            <span>
              <kbd className="font-sans font-semibold">esc</kbd> close
            </span>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Binds ⌘K / Ctrl-K globally.
 *
 * Ignores the shortcut while focus is in a text field, so a palette does not
 * hijack a keystroke someone meant for the document they are editing.
 */
export function useCommandPaletteShortcut(onOpen: () => void): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      e.preventDefault();
      onOpen();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpen]);
}

/** Convenience for building navigation entries from the nav model. */
export function useSectionCommands(
  sections: ReadonlyArray<{ to: string; label: string; icon?: IconName }>,
): CommandItem[] {
  const navigate = useNavigate();
  return useMemo(
    () =>
      sections.map((section) => ({
        id: `nav:${section.to}`,
        label: section.label,
        icon: section.icon,
        group: 'Go to' as const,
        onSelect: () => void navigate({ to: section.to }),
      })),
    [sections, navigate],
  );
}

export type { ReactNode };
